import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import './gas-price.scss';
import CountUp from 'react-countup';
import { useRangeValues } from 'src/custom-hooks/use-range-values';
import { usePriceType } from 'src/custom-hooks/use-price-type';
import type { IGasPriceToPreview, ICryptoCurrency, ITransferFees } from '../../interfaces/gas-price.model';
import { getLocalizedMessage } from '../../helpers/localizations';
import { formatNumber } from '../../helpers/format-number';
import { TimeSvg } from '../time-icon/TimeIcon';
import { Icon } from '../svg-icon/SvgIcon';
import { useImage } from '../../custom-hooks/use-image';

export function GasPrice({
  gasPrice,
  localizations,
  loading,
  tokenPrice,
  selectedNetwork,
}: {
  gasPrice: IGasPriceToPreview;
  localizations: Map<string, string>;
  loading: boolean;
  tokenPrice: ITransferFees | null;
  selectedNetwork: ICryptoCurrency | undefined;
}) {
  const [isSingleType, setIsSingleType] = useState<boolean>(false);
  const image = useImage(gasPrice, isSingleType);
  const { rangeFiat, rangeNative } = useRangeValues(gasPrice, loading);
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const localizedMessage = (key: string): string => getLocalizedMessage(key, localizations);
  const priceType = usePriceType(gasPrice, localizedMessage);

  useEffect(() => {
    const types = ['slow', 'optimal', 'fast'];
    const availableTypes = types.filter((type) => gasPrice[type] != null);
    setIsSingleType(availableTypes.length === 1);
  }, [gasPrice]);

  const getNativePriceClassName = (price: number): string => {
    const formattedPrice = formatNumber(price);
    let result = 'native-price';
    if (formattedPrice.length === 7) {
      result += ' font-size-big';
    }
    if (formattedPrice.length > 7) {
      result += ' font-size-normal';
    }
    return result;
  };

  const correctedType = gasPrice.type === 'normal' ? 'optimal' : gasPrice.type;
  const tokenKey = `tokenTransfer${correctedType.charAt(0).toUpperCase()}${correctedType.slice(1)}`;
  const fiatKey = `fiatTransfer${correctedType.charAt(0).toUpperCase()}${correctedType.slice(1)}`;

  const isDisplayTokenPrice = tokenPrice != null;
  const nativePrice = isDisplayTokenPrice && tokenPrice?.data?.[tokenKey] != null ? parseFloat(tokenPrice.data[tokenKey]) : gasPrice.price;

  const fiatPrice = isDisplayTokenPrice && tokenPrice?.data?.[fiatKey] != null ? parseFloat(tokenPrice.data[fiatKey]) : gasPrice.fiatPrice;

  const units = isDisplayTokenPrice ? tokenPrice.data.native : gasPrice.units;
  const selectedNetworkImg = selectedNetwork?.img ?? null;
  const isGasPriceOneType = gasPrice.count != null && gasPrice.count === 1;

  const processingTime = (
    <div className={`processing-time ${gasPrice.type}`}>
      <div className="time-icon">
        <TimeSvg />
      </div>
      <span>
        {gasPrice.time} {localizedMessage(`calculator__time-${gasPrice.timeUnit}`)}
      </span>
    </div>
  );

  const getImage = (): JSX.Element => {
    if (image != null && image !== '') {
      return <img alt="" className="image" src={image} />;
    }
    if (isGasPriceOneType) {
      return <img className="gas-price__content-img" src={selectedNetworkImg} alt="" />;
    }

    return <Icon className="image" name={gasPrice.type} />;
  };

  return (
    <Sentry.ErrorBoundary>
      <div className={`gas-price__content ${isGasPriceOneType ? 'gas-price__content--one-type' : ''}`}>
        {isMobile ? (
          <>
            <div className="gas-price__content--left-side">{getImage()}</div>
            <div className="gas-price__content--right-side">
              <div className="type">{priceType}</div>

              <div className="native-price">
                <CountUp start={rangeNative?.start} end={nativePrice} decimal="." decimals={10} formattingFn={formatNumber} />
                &nbsp;{units}
              </div>
              <div className="fiat-price">
                <CountUp start={rangeFiat?.start} end={fiatPrice} decimal="." decimals={10} formattingFn={formatNumber} />
                &nbsp;{gasPrice.fiatUnits}
              </div>
              {!isGasPriceOneType && processingTime}
            </div>
          </>
        ) : (
          <>
            <div className={getNativePriceClassName(nativePrice)}>
              <CountUp start={rangeNative?.start} end={nativePrice} decimal="." decimals={10} formattingFn={formatNumber} />
              &nbsp;{units}
            </div>
            <div className="fiat-price">
              <CountUp start={rangeFiat?.start} end={fiatPrice} decimal="." decimals={10} formattingFn={formatNumber} />
              &nbsp;{gasPrice.fiatUnits}
            </div>
            {getImage()}
            {isGasPriceOneType ? (
              localizedMessage('calculator__speed-average')
            ) : (
              <div className="type">{localizedMessage(`calculator__speed-${gasPrice.type === 'normal' ? 'optimal' : gasPrice.type}`)}</div>
            )}
            {!isGasPriceOneType && processingTime}
          </>
        )}
      </div>
    </Sentry.ErrorBoundary>
  );
}
